import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CalendarClock, Search, Loader2, AlertTriangle, CheckCircle2, Circle, Phone, Mail, Building2 } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const todayStr = () => new Intl.DateTimeFormat("en-CA", { timeZone: "America/New_York", year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date());
const parseLocal = (s) => { if (!s) return null; const [y, m, d] = s.slice(0, 10).split("-").map(Number); return new Date(y, m - 1, d); };
const toKey = (date) => date.toLocaleDateString("en-CA");
const fmtDate = (s) => { const d = parseLocal(s); return d ? d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" }) : "—"; };
const stageLabel = (s) => ({ new: "New", contacted: "Contacted", proposal_sent: "Proposal Sent", won: "Won", lost: "Lost" }[s] || "New");

function statusOf(task) {
  if (task.completed) return { id: "done", label: "Done", cls: "bg-slate-100 text-slate-500" };
  const due = (task.due_date || "").slice(0, 10);
  if (due && due < todayStr()) return { id: "overdue", label: "Overdue", cls: "bg-red-100 text-red-700" };
  if (due === todayStr()) return { id: "today", label: "Due today", cls: "bg-emerald-100 text-emerald-700" };
  return { id: "upcoming", label: "Upcoming", cls: "bg-slate-100 text-slate-600" };
}

export default function FollowUpsPanel({ token, onExpire, onChanged }) {
  const [tasks, setTasks] = useState(null);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("attention");
  const [selectedDay, setSelectedDay] = useState(null);
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState(null);

  const headers = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [tr, lr] = await Promise.all([
        axios.get(`${API}/admin/tasks`, headers),
        axios.get(`${API}/admin/quotes`, headers),
      ]);
      setTasks(tr.data.filter((t) => t.kind === "follow_up"));
      setLeads(lr.data);
    } catch (e) {
      if (e.response?.status === 401) onExpire?.();
      else toast.error("Failed to load follow-ups.");
    } finally { setLoading(false); }
  }, [headers, onExpire]);

  useEffect(() => { load(); }, [load]);

  const leadById = useMemo(() => Object.fromEntries(leads.map((l) => [l.id, l])), [leads]);
  const stats = useMemo(() => {
    const active = (tasks || []).filter((t) => !t.completed);
    return {
      overdue: active.filter((t) => t.due_date < todayStr()).length,
      today: active.filter((t) => t.due_date === todayStr()).length,
      upcoming: active.filter((t) => t.due_date > todayStr()).length,
      done: (tasks || []).filter((t) => t.completed).length,
    };
  }, [tasks]);

  const visible = useMemo(() => {
    let list = [...(tasks || [])];
    const q = query.trim().toLowerCase();
    if (selectedDay) {
      const key = toKey(selectedDay);
      list = list.filter((t) => (t.due_date || "").slice(0, 10) === key);
    } else if (filter === "attention") list = list.filter((t) => !t.completed && t.due_date <= todayStr());
    else if (filter === "upcoming") list = list.filter((t) => !t.completed && t.due_date > todayStr());
    else if (filter === "completed") list = list.filter((t) => t.completed);
    if (q) {
      list = list.filter((t) => {
        const l = leadById[t.lead_id] || {};
        return [t.title, t.notes, l.name, l.business, l.phone, l.email, l.service_address].some((v) => (v || "").toLowerCase().includes(q));
      });
    }
    return list.sort((a, b) => (a.due_date || "9999").localeCompare(b.due_date || "9999") || a.title.localeCompare(b.title));
  }, [tasks, filter, selectedDay, query, leadById]);

  const dueDates = useMemo(() => (tasks || []).filter((t) => !t.completed && t.due_date).map((t) => parseLocal(t.due_date)).filter(Boolean), [tasks]);
  const overdueDates = useMemo(() => (tasks || []).filter((t) => !t.completed && t.due_date < todayStr()).map((t) => parseLocal(t.due_date)).filter(Boolean), [tasks]);

  const toggle = async (task) => {
    setBusy(task.id);
    try {
      const { data } = await axios.patch(`${API}/admin/tasks/${task.id}`, { completed: !task.completed }, headers);
      setTasks((p) => p.map((t) => (t.id === task.id ? data : t)));
      onChanged?.();
      toast.success(data.completed ? "Follow-up marked done" : "Follow-up reopened");
    } catch (e) {
      if (e.response?.status === 401) onExpire?.();
      else toast.error("Could not update follow-up.");
    } finally { setBusy(null); }
  };

  const filterBtn = (id, label, count) => (
    <button key={id} type="button" onClick={() => { setFilter(id); setSelectedDay(null); }} data-testid={`followups-filter-${id}`}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-colors ${filter === id && !selectedDay ? "bg-[#0E3B3B] text-white" : "bg-white border border-[#E7E5DF] text-slate-600 hover:border-emerald-500"}`}>
      {label}<span className={`grid place-items-center min-w-4 h-4 px-1 rounded-full text-[10px] ${filter === id && !selectedDay ? "bg-white/20" : "bg-slate-100"}`}>{count}</span>
    </button>
  );

  if (loading && !tasks) return <div className="grid place-items-center py-20 text-slate-500" data-testid="followups-loading"><Loader2 size={28} className="animate-spin" /></div>;

  return (
    <div className="space-y-5" data-testid="followups-panel">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-3">
          <span className="grid place-items-center w-10 h-10 rounded-xl bg-[#0E3B3B] text-emerald-300"><CalendarClock size={19} /></span>
          <div>
            <h2 className="font-display text-xl font-extrabold text-[#0B0F14]">Customer Follow-Ups</h2>
            <p className="text-xs text-slate-500">Internal reminders only · owner email at 8:00 AM Eastern</p>
          </div>
        </div>
        <div className="relative ml-auto w-full sm:w-72">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search follow-ups…" data-testid="followups-search"
            className="w-full pl-9 pr-3 py-2.5 rounded-xl bg-white border border-[#E7E5DF] text-sm focus:outline-none focus:border-emerald-600/60" />
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="followups-stats">
        {[["Overdue", stats.overdue, "text-red-600"], ["Due today", stats.today, "text-emerald-700"], ["Upcoming", stats.upcoming, "text-[#0E3B3B]"], ["Completed", stats.done, "text-slate-500"]].map(([label, count, cls]) => (
          <div key={label} className="rounded-2xl border border-[#E7E5DF] bg-white p-4 shadow-soft" data-testid={`followups-stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
            <p className="font-mono-tech text-[10px] uppercase tracking-wider text-slate-400">{label}</p>
            <p className={`mt-1 font-display text-3xl font-extrabold ${cls}`}>{count}</p>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-[340px_1fr] gap-5 items-start">
        <div className="rounded-2xl border border-[#E7E5DF] bg-white p-3 shadow-soft" data-testid="followups-calendar">
          <Calendar mode="single" selected={selectedDay} onSelect={(d) => setSelectedDay(d || null)}
            modifiers={{ due: dueDates, overdue: overdueDates }}
            modifiersClassNames={{
              due: "relative after:content-[''] after:absolute after:bottom-1 after:left-1/2 after:-translate-x-1/2 after:w-1.5 after:h-1.5 after:rounded-full after:bg-emerald-500",
              overdue: "relative after:content-[''] after:absolute after:bottom-1 after:left-1/2 after:-translate-x-1/2 after:w-1.5 after:h-1.5 after:rounded-full after:bg-red-500",
            }}
            className="w-full [&_.rdp-day_selected]:bg-emerald-600 [&_.rdp-day_selected]:text-white" />
          <div className="flex items-center gap-4 px-2 pb-1 text-[11px] text-slate-500">
            <span className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-emerald-500" /> Due</span>
            <span className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-red-500" /> Overdue</span>
          </div>
        </div>

        <div className="rounded-2xl border border-[#E7E5DF] bg-white shadow-soft overflow-hidden" data-testid="followups-table-card">
          <div className="flex items-center gap-2 overflow-x-auto no-scrollbar p-3 border-b border-[#E7E5DF]">
            {selectedDay ? (
              <button type="button" onClick={() => setSelectedDay(null)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-600 text-white text-xs font-semibold" data-testid="followups-clear-day">
                <CalendarClock size={13} /> {selectedDay.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
              </button>
            ) : (
              <>{filterBtn("attention", "Needs attention", stats.overdue + stats.today)}{filterBtn("upcoming", "Upcoming", stats.upcoming)}{filterBtn("completed", "Completed", stats.done)}{filterBtn("all", "All", tasks?.length || 0)}</>
            )}
          </div>
          {visible.length === 0 ? (
            <div className="p-10 text-center text-slate-400 text-sm" data-testid="followups-empty">
              {selectedDay ? "No follow-ups on this date." : "No follow-ups match this view."}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-[820px] w-full text-sm" data-testid="followups-table">
                <thead className="bg-[#FBFAF8] text-left font-mono-tech text-[10px] uppercase tracking-wider text-slate-500">
                  <tr><th className="px-4 py-3">Done</th><th className="px-4 py-3">Customer</th><th className="px-4 py-3">Due</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Stage</th><th className="px-4 py-3">Contact</th></tr>
                </thead>
                <tbody className="divide-y divide-[#F0EFEA]">
                  {visible.map((task) => {
                    const lead = leadById[task.lead_id] || {};
                    const st = statusOf(task);
                    const who = lead.name || task.title.replace(/^Follow up:\s*/, "");
                    return (
                      <tr key={task.id} className={task.completed ? "opacity-60" : ""} data-testid={`followup-row-${task.id}`}>
                        <td className="px-4 py-3">
                          <button type="button" onClick={() => toggle(task)} disabled={busy === task.id} data-testid={`followup-toggle-${task.id}`} className="text-emerald-600 disabled:opacity-40" title={task.completed ? "Reopen" : "Mark done"}>
                            {busy === task.id ? <Loader2 size={20} className="animate-spin" /> : task.completed ? <CheckCircle2 size={21} /> : <Circle size={21} className="text-slate-300" />}
                          </button>
                        </td>
                        <td className="px-4 py-3 min-w-[190px]">
                          <p className="font-semibold text-[#0B0F14]">{who}</p>
                          {lead.business && <p className="flex items-center gap-1 text-xs text-slate-500 mt-0.5"><Building2 size={11} /> {lead.business}</p>}
                          {task.notes && <p className="text-[11px] text-slate-400 mt-1 line-clamp-2">{task.notes}</p>}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-slate-600">{fmtDate(task.due_date)}</td>
                        <td className="px-4 py-3"><span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-bold ${st.cls}`} data-testid={`followup-status-${task.id}`}>{st.id === "overdue" && <AlertTriangle size={11} />}{st.label}</span></td>
                        <td className="px-4 py-3 text-slate-600">{stageLabel(lead.stage)}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            {lead.phone && <a href={`tel:${lead.phone}`} className="grid place-items-center w-8 h-8 rounded-full bg-emerald-50 text-emerald-700" data-testid={`followup-call-${task.id}`} aria-label={`Call ${who}`}><Phone size={14} /></a>}
                            {lead.email && <a href={`mailto:${lead.email}`} className="grid place-items-center w-8 h-8 rounded-full bg-slate-100 text-slate-600" data-testid={`followup-email-${task.id}`} aria-label={`Email ${who}`}><Mail size={14} /></a>}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
